import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Stack } from "@mui/material";

import messages from "../../../../assets/locale/messages";
import { UIButton, UIDatePicker, UIModal } from "../../../../components/ui";
import { SA_UTC_OFFSET_MINUTES } from "../../../../utils/Constants";
import { endNotBeforeStart } from "../../../../utils/dateRangeValidation";
import {
  addTrainingWorkingSlot,
  editTrainingWorkingSlotRequest,
} from "../../../../store/TrainingWorkingSlots/actions";
import "../ApplicantReportModal/ApplicantReportModal.scss";

dayjs.extend(utc);

const formatDateTimeForApi = (selectedDate) =>
  selectedDate
    ? dayjs(selectedDate).utcOffset(SA_UTC_OFFSET_MINUTES).format()
    : "";

const TrainingCheckinModal = ({
  open,
  handleClose,
  userName,
  selectedItem,
  eventId,
  userId,
}) => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { shared, timeSheet, inputsValidations } = messages[lang];
  const isEditMode = !!selectedItem;

  const initialValues = {
    check_in_at: selectedItem?.check_in_at || "",
    check_out_at: selectedItem?.check_out_at || "",
  };

  const handleEditTrainingWorkingSlot = (formValues) => {
    dispatch(
      editTrainingWorkingSlotRequest({
        id: selectedItem.id,
        data: {
          check_in_at: formValues.check_in_at,
          check_out_at: formValues.check_out_at || null,
        },
        event_id: eventId,
        user_id: userId,
      })
    );
  };

  const handleAddTrainingWorkingSlot = (formValues) => {
    dispatch(
      addTrainingWorkingSlot({
        data: {
          check_in_at: formValues.check_in_at,
          check_out_at: formValues.check_out_at || null,
          event_id: eventId,
          user_id: userId,
        },
      })
    );
  };

  const {
    handleSubmit,
    values,
    setFieldTouched,
    setFieldValue,
    setValues,
    touched,
    errors,
    dirty,
    isValid,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema: Yup.object({
      check_in_at: Yup.string().required("generalFieldRequired"),
      check_out_at: Yup.string()
        .optional()
        .nullable()
        .test(
          "checkout-after-checkin",
          "checkoutCannotBeBeforeCheckin",
          endNotBeforeStart("check_in_at")
        ),
    }),
    onSubmit: async (formValues) => {
      if (isEditMode) {
        handleEditTrainingWorkingSlot(formValues);
      } else {
        handleAddTrainingWorkingSlot(formValues);
      }
      handleClose(false);
    },
  });

  const handleCloseStatusModal = () => {
    resetForm();
    handleClose(false);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    } else {
      setValues(initialValues);
    }
  }, [open, selectedItem]);

  const resolveFieldError = (field) =>
    touched[field] && errors[field]
      ? inputsValidations[errors[field]] || errors[field]
      : undefined;

  const handleDateTimeChange = (field) => (_formatted, nextValue) => {
    setFieldTouched(field);
    setFieldValue(field, formatDateTimeForApi(nextValue));
  };

  const modalTitle = isEditMode
    ? `${timeSheet.applicantReport.editTrainingStatusFor} ${userName}`
    : `${timeSheet.applicantReport.addTrainingCheckinFor} ${userName}`;

  const dateTimeInputFormat =
    lang === "ar" ? "YYYY/MM/DD hh:mm A" : "DD/MM/YYYY hh:mm A";

  return (
    <UIModal
      open={open}
      onClose={handleCloseStatusModal}
      title={modalTitle}
      maxWidth="md"
      showClose={false}
      PaperProps={{ className: "applicant-report-modal" }}
      actions={
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          width="100%"
          className="applicant-report-modal__actions"
        >
          <UIButton
            type="submit"
            form="training-checkin-form"
            appearance="gradient"
            disabled={!dirty || !isValid}
          >
            {shared.save}
          </UIButton>
          <UIButton
            type="button"
            variant="outlined"
            color="inherit"
            onClick={handleCloseStatusModal}
          >
            {shared.cancel}
          </UIButton>
        </Stack>
      }
    >
      <form
        id="training-checkin-form"
        className="applicant-report-modal__form"
        noValidate
        onSubmit={handleSubmit}
      >
        <UIDatePicker
          id="training-check-in"
          name="check_in_at"
          withTime
          disableFuture
          label={timeSheet.checkin}
          placeholder={timeSheet.checkinPlaceHolder}
          value={values.check_in_at}
          required
          inputFormat={dateTimeInputFormat}
          error={Boolean(touched.check_in_at && errors.check_in_at)}
          helperText={resolveFieldError("check_in_at")}
          onChange={handleDateTimeChange("check_in_at")}
          classNames={{
            label: "applicant-report-modal__check-in-label",
          }}
        />
        <UIDatePicker
          id="training-check-out"
          name="check_out_at"
          withTime
          disableFuture
          label={timeSheet.checkout}
          placeholder={timeSheet.checkoutPlaceHolder}
          value={values.check_out_at}
          inputFormat={dateTimeInputFormat}
          error={Boolean(touched.check_out_at && errors.check_out_at)}
          helperText={resolveFieldError("check_out_at")}
          onChange={handleDateTimeChange("check_out_at")}
          classNames={{
            label: "applicant-report-modal__check-out-label",
          }}
        />
      </form>
    </UIModal>
  );
};

TrainingCheckinModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  userName: PropTypes.string,
  selectedItem: PropTypes.object,
  eventId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
};

export default TrainingCheckinModal;
